import { CalendarDays, Filter } from 'lucide-react'
import { Card } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useCompanies } from '@/services/admin/companies.service'
import { useAdminCourses } from '@/services/admin/courses.service'
import { MultiCompanySelect } from './MultiCompanySelect'
import type { DashboardFilters as DashboardFiltersType } from './types'

interface Props {
  filters: DashboardFiltersType
  onChange: (next: DashboardFiltersType) => void
}

/** Pre-sets de período. Calculados na hora de selecionar. */
const PERIOD_PRESETS = [
  { value: '7d', label: 'Últimos 7 dias', days: 7 },
  { value: '30d', label: 'Últimos 30 dias', days: 30 },
  { value: '90d', label: 'Últimos 90 dias', days: 90 },
  { value: 'all', label: 'Todo o período', days: null },
] as const

type PresetValue = (typeof PERIOD_PRESETS)[number]['value']

/**
 * Barra de filtros globais do dashboard: empresa, curso, período.
 * Mudança em qualquer filtro propaga via onChange — pai (DashboardSuperAdmin)
 * passa esses filters pra TODOS os charts.
 */
export function DashboardFilters({ filters, onChange }: Props) {
  const { data: companies } = useCompanies()
  const { data: courses } = useAdminCourses()

  // Resolve qual preset está ativo baseado em filters.dateFrom
  const activePreset: PresetValue = (() => {
    if (!filters.dateFrom) return 'all'
    const days = Math.round((Date.now() - filters.dateFrom.getTime()) / (1000 * 60 * 60 * 24))
    if (days <= 8) return '7d'
    if (days <= 31) return '30d'
    if (days <= 91) return '90d'
    return 'all'
  })()

  function setPreset(p: PresetValue) {
    const preset = PERIOD_PRESETS.find((x) => x.value === p)!
    if (preset.days === null) {
      onChange({ ...filters, dateFrom: undefined, dateTo: undefined })
    } else {
      const dateTo = new Date()
      const dateFrom = new Date(Date.now() - preset.days * 24 * 60 * 60 * 1000)
      onChange({ ...filters, dateFrom, dateTo })
    }
  }

  return (
    <Card className="glass-card border-border/50 p-4">
      <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold">
        <Filter className="w-3.5 h-3.5" />
        Filtros
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Empresa (múltipla seleção) */}
        <MultiCompanySelect
          companies={companies ?? []}
          value={filters.companyIds ?? []}
          onChange={(ids) => onChange({ ...filters, companyIds: ids.length ? ids : undefined })}
        />

        {/* Curso */}
        <Select
          value={filters.courseId ? String(filters.courseId) : '__all__'}
          onValueChange={(v) =>
            onChange({
              ...filters,
              courseId: v === '__all__' ? undefined : Number(v),
            })
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Todos os cursos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos os cursos</SelectItem>
            {courses?.map((c) => (
              <SelectItem key={c.id} value={String(c.id)}>
                {c.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Período */}
        <Select value={activePreset} onValueChange={(v) => setPreset(v as PresetValue)}>
          <SelectTrigger>
            <CalendarDays className="w-3.5 h-3.5 mr-2 inline-block" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIOD_PRESETS.map((p) => (
              <SelectItem key={p.value} value={p.value}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </Card>
  )
}
